// ─── useCustomerBank · POS Control Sprint (Cleanup #2) ────────────────
// Single source of truth for guest lookup inside the POS terminal.
//
// Before this hook, phone-based guest lookup was duplicated inline in:
//   • BillingScreen.js  (Address takeaway/room-service capture)
//   • FloorView.js      (Take-away punch-in dialog)
// Both sites re-implemented the same "strip non-digits → lookup → hydrate
// name/address" flow. This hook consolidates the pattern so that when we
// swap the mock catalogue for a live `/api/customers/lookup` endpoint the
// change lives in exactly one file.
//
// Scope note (Sprint decision · Feb 2026): the hook is deliberately
// POS-scoped. AdminCustomers, AdminReservations and AdminLoyalty each
// hit their own well-established live APIs today and do not share the
// mock catalogue. Migrating them into this hook is a P1 follow-up
// tracked as "Unify guest bank across POS + admin". Extending this hook
// then only needs a new `search()` implementation — the call sites do
// not change.
//
// Contract:
//   const bank = useCustomerBank();
//   const g = bank.lookup(phone);   // { name, phones?, addresses? } | null
//   bank.upsert({ phone, name, address, tags: ["takeaway"] });
//                                   // no-op in mock mode; wired to
//                                   // /api/customers/upsert when live
//
// Values returned from lookup() are stable object refs suitable to feed
// directly into setName / setAddress state.

import { useCallback, useMemo } from "react";
import { lookupGuestByPhone } from "./mockData";

/** Normalise any input to a digits-only phone key (empty string on nil). */
export const normalisePhone = (p) => String(p || "").replace(/[^0-9]/g, "");

export function useCustomerBank() {
  // The mock catalogue is a module-level constant — memoising `lookup`
  // keeps its identity stable across renders so effects that depend on
  // it don't loop.
  const lookup = useCallback((phone) => {
    const digits = normalisePhone(phone);
    if (digits.length < 4) return null;
    return lookupGuestByPhone(digits) || null;
  }, []);

  // Upsert is a no-op in mock mode. When the live customer bank lands
  // this will POST to `/api/customers/upsert` with { phone, name,
  // address, tags[] }; every caller keeps the SAME signature so no
  // consumer needs to change.
  const upsert = useCallback((_payload) => {
    // Deliberately empty: the mock catalogue is read-only. Tags carry
    // through to the order object today; that will remain the source
    // for aggregate reporting until the live bank exists.
    return Promise.resolve({ ok: true });
  }, []);

  return useMemo(() => ({ lookup, upsert, normalisePhone }), [lookup, upsert]);
}
